import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/map");
    } catch (e: any) {
      setErr(e?.detail || e?.message || "Помилка входу");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background:
          "radial-gradient(circle at 30% 20%, #ebf4ff 0%, transparent 45%), " +
          "radial-gradient(circle at 70% 80%, #e6fffa 0%, transparent 45%), " +
          "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
        position: "relative",
      }}
    >
      <form
        onSubmit={onSubmit}
        className="animate-scale-in"
        style={{
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          padding: 36,
          width: 420,
          maxWidth: "92vw",
          borderRadius: 12,
          boxShadow: "var(--sh-xl)",
          border: "1px solid rgba(255, 255, 255, 0.8)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #2c5282, #4299e1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 28,
              fontWeight: 700,
              marginBottom: 12,
              boxShadow: "0 8px 24px rgba(44, 82, 130, 0.3)",
            }}
          >
            ⊕
          </div>
          <h1 style={{ fontSize: 22, color: "var(--c-text)", margin: "0 0 4px", letterSpacing: -0.3 }}>
            HRS — Hospital Rating System
          </h1>
          <p style={{ color: "var(--c-text-muted)", fontSize: 13, margin: 0 }}>
            Рейтинг лікарень України за відкритими даними НСЗУ
          </p>
        </div>

        <Field label="Email" icon={<Mail size={14} />}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@feodal.online"
            style={{ width: "100%", padding: "10px 12px 10px 36px", fontSize: 14 }}
          />
        </Field>

        <Field label="Пароль" icon={<Lock size={14} />}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder="••••••••"
            style={{ width: "100%", padding: "10px 12px 10px 36px", fontSize: 14 }}
          />
        </Field>

        {err && (
          <div
            className="animate-slide-up"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              background: "var(--c-danger-soft)",
              border: "1px solid #feb2b2",
              color: "#742a2a",
              borderRadius: 6,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <AlertCircle size={14} style={{ flexShrink: 0 }} /> {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: 22,
            width: "100%",
            padding: "12px 16px",
            background: loading
              ? "var(--c-text-dim)"
              : "linear-gradient(135deg, #2c5282 0%, #2a4a73 100%)",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 14,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            boxShadow: loading ? "none" : "0 4px 14px rgba(44, 82, 130, 0.35)",
            transition: "all var(--t-fast)",
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <LogIn size={15} />
          {loading ? "Вхід…" : "Увійти"}
        </button>

        <p
          style={{
            marginTop: 18,
            textAlign: "center",
            fontSize: 12,
            color: "var(--c-text-dim)",
          }}
        >
          Забули пароль? Зверніться до адміністратора.
        </p>
      </form>
    </div>
  );
}

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--c-text-2)",
          marginBottom: 6,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--c-text-muted)",
            pointerEvents: "none",
          }}
        >
          {icon}
        </span>
        {children}
      </div>
    </div>
  );
}
